/* Konvexe Körper aus Seiten (Polygonen) für die Dekompilierung: Schneiden an Ebenen, Volumen. Reine Geometrie ohne DOM. */
import type { Vec3 } from './types.ts';

export type Poly = Vec3[];
/** Seite eines Körpers: Normale n (nach außen), Abstand d (n·p = d) und die Eckpunkte */
export interface Side { n: Vec3; d: number; pts: Vec3[] }
export type Body = Side[];

export const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
export const len = (a: Vec3): number => Math.sqrt(dot(a, a));
export const unit = (a: Vec3): Vec3 => { const l = len(a); return l > 1e-12 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 0, 1]; };
export const EPS = 0.01;

export function polyArea(pts: Vec3[]): number {
  let a: Vec3 = [0, 0, 0];
  for (let i = 1; i < pts.length - 1; i++) { const c = cross(sub(pts[i], pts[0]), sub(pts[i + 1], pts[0])); a = add(a, c); }
  return len(a) / 2;
}

/** Polygon an der Ebene n·p = d schneiden, behalten wird n·p <= d. */
export function clipPoly(pts: Vec3[], n: Vec3, d: number): Vec3[] {
  const out: Vec3[] = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length], da = dot(n, a) - d, db = dot(n, b) - d;
    if (da <= EPS) out.push(a);
    if ((da < -EPS && db > EPS) || (da > EPS && db < -EPS)) {
      const t = da / (da - db); out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]);
    }
  }
  return out;
}

/** Körper = Liste von Seiten { n, d, pts }, n zeigt nach außen (n·p = d). */
export function boxBody(mn: Vec3, mx: Vec3): Body {
  const f = (n: Vec3, d: number, pts: Vec3[]): Side => ({ n: n, d: d, pts: pts });
  const [x0, y0, z0] = mn, [x1, y1, z1] = mx;
  return [
    f([1, 0, 0], x1, [[x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [x1, y0, z1]]),
    f([-1, 0, 0], -x0, [[x0, y1, z0], [x0, y0, z0], [x0, y0, z1], [x0, y1, z1]]),
    f([0, 1, 0], y1, [[x1, y1, z0], [x0, y1, z0], [x0, y1, z1], [x1, y1, z1]]),
    f([0, -1, 0], -y0, [[x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]]),
    f([0, 0, 1], z1, [[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]]),
    f([0, 0, -1], -z0, [[x0, y1, z0], [x1, y1, z0], [x1, y0, z0], [x0, y0, z0]])
  ];
}

/** Körper an der Ebene schneiden und die Seite n·p <= d behalten. Null, wenn nichts übrig bleibt. */
export function clipBody(body: Body, n: Vec3, d: number): Body | null {
  let anyOut = false, anyIn = false;
  for (const f of body) for (const p of f.pts) { const x = dot(n, p) - d; if (x > EPS) anyOut = true; else if (x < -EPS) anyIn = true; }
  if (!anyOut) return body;
  if (!anyIn) return null;
  const out: Body = [], capPts: Vec3[] = [];
  for (const f of body) {
    const np = clipPoly(f.pts, n, d);
    if (np.length >= 3 && polyArea(np) > 0.01) out.push({ n: f.n, d: f.d, pts: np });
    for (const p of np) if (Math.abs(dot(n, p) - d) <= 2 * EPS) capPts.push(p);
  }
  // Deckfläche: Punkte auf der Schnittebene ordnen
  const uniq: Vec3[] = [];
  capPts.forEach((p) => { if (!uniq.some((q) => Math.abs(p[0] - q[0]) + Math.abs(p[1] - q[1]) + Math.abs(p[2] - q[2]) < 0.02)) uniq.push(p); });
  if (uniq.length < 3) return null;
  const c = uniq.reduce((s, p) => add(s, p), [0, 0, 0] as Vec3).map((x) => x / uniq.length) as Vec3;
  const ax: Vec3 = Math.abs(n[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0], u = unit(cross(n, ax)), v = cross(n, u);
  uniq.sort((p, q) => Math.atan2(dot(sub(p, c), v), dot(sub(p, c), u)) - Math.atan2(dot(sub(q, c), v), dot(sub(q, c), u)));
  if (polyArea(uniq) < 0.01) return null;
  out.push({ n: n, d: d, pts: uniq });
  return out.length >= 4 ? out : null;
}

export function bodyVolume(body: Body): number {
  let v = 0;
  body.forEach((f) => { for (let i = 1; i < f.pts.length - 1; i++) v += dot(f.pts[0], cross(f.pts[i], f.pts[i + 1])) / 6; });
  return Math.abs(v);
}

